import { useState } from "react";
import { Modal, Button } from "flowbite-react";
import MaintenanceHistoryTable from "../tables/MaintenanceHistoryTable";
import {
  Budgetlabel,
  IContestation,
  IMaintenance,
  IMaintenanceHistoryItem,
  ProductOrServiceType,
} from "@/types/maintenance.type";
import {
  confirmMaintenanceCharge,
  contestMaintenanceCharge,
  MaintenanceHistoryTypeUpdate,
} from "@/common/services/maintenance.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { chargesDendDispute } from "@/common/services/partner-charge.api";

type Props = {
  onClosed: () => void;
  licensePlate?: string;
  maintenanceId?: string;
  companyId?: string;
  chargeStatus?: IMaintenance["chargeMaintenance"];
  chargeId?: string;
  setReload: (boolean) => void;
  reload: boolean;
};

function ToCheckModal(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fullHistory, setFullHistory] = useState<IMaintenanceHistoryItem[]>([]);
  const [historyChanges, setHistoryChanges] = useState<
    IMaintenanceHistoryItem[]
  >([]);
  const [contestObservation, setContestObservation] = useState("");

  if (!props.licensePlate || !props.maintenanceId) {
    return null;
  }

  const areActionsDisabled =
    props.chargeStatus?.confirmed || props.chargeStatus?.contested;

  const hasHistoryChanges = historyChanges.length > 0;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const result = await confirmMaintenanceCharge(
        props.maintenanceId as string
      );
      if (result.ok) {
        notifySuccess("Manutenção confirmada com sucesso!");
        props.onClosed();
      } else {
        notifyError(result.message || "Falha ao confirmar manutenção.");
      }
    } catch {
      notifyError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      props.setReload(!props.reload);
      setIsSubmitting(false);
    }
  };

  const buildItemsDescription = () => {
    if (!historyChanges.length) {
      return "";
    }

    return historyChanges
      .map((item) => {
        const typeLabelMap: Record<string, string> = {
          preventive: "Preventiva",
          corrective: "Corretiva",
          misuse: "Mau uso",
        };

        const previousTypeLabel = item.previousType
          ? typeLabelMap[item.previousType] || item.previousType
          : "-";

        const newTypeLabel = typeLabelMap[item.newType] || item.newType;

        return [
          `Peça: ${item.partReplaced ?? item.itemId}`,
          `Tipo anterior: ${previousTypeLabel}`,
          `Tipo atual: ${newTypeLabel}`,
          item.quantity !== undefined ? `Quantidade: ${item.quantity}` : null,
          item.lastReplacementDate
            ? `Data última troca: ${item.lastReplacementDate}`
            : null,
          item.kmAtLastReplacement !== undefined &&
            item.kmAtLastReplacement !== null
            ? `KM última troca: ${item.kmAtLastReplacement}`
            : null,
        ]
          .filter(Boolean)
          .join(" | ");
      })
      .join("\n");
  };

  const handleContest = async () => {
    if (!props.maintenanceId) {
      notifyError("ID da manutenção não encontrado.");
      return;
    }

    if (!props.companyId) {
      notifyError("ID da empresa não encontrado.");
      return;
    }

    if (!hasHistoryChanges) {
      notifyError("Nenhuma alteração detectada para contestar.");
      return;
    }

    if (!contestObservation.trim()) {
      notifyError("Informe uma observação para contestar.");
      return;
    }
    const updatesForApi: MaintenanceHistoryTypeUpdate[] = historyChanges.map(
      (item) => ({
        historyItemId: item._id,
        newType: item.newType,
      })
    );
    if (updatesForApi.some((item) => !item.historyItemId)) {
      notifyError(
        "Erro: Não foi possível identificar um dos itens do histórico (ID faltando)."
      );
      return; // Para a execução
    }
    const budgetMaintenance: ProductOrServiceType = {
      corrective: [],
      preventive: [],
      misuse: [],
    };

    fullHistory.forEach((item) => {
      const budgetItem: Budgetlabel = {
        name: item.partReplaced,
        productOrWorkId: item._id,
        quantity: item.quantity,
        type: item.type,
        sellPrice: item.sellPrice || 0,
        code: item.code || "",
      };

      if (item.type === "corrective") {
        budgetMaintenance.corrective.push(budgetItem);
      } else if (item.type === "preventive") {
        budgetMaintenance.preventive.push(budgetItem);
      } else if (item.type === "misuse") {
        budgetMaintenance.misuse.push(budgetItem);
      }
    });
    const itemsDescription = buildItemsDescription();

    const fullMessage = itemsDescription
      ? `${contestObservation}\n\nItens contestados:\n${itemsDescription}`
      : contestObservation;
    const contestationData: IContestation = {
      date: new Date(),
      message: fullMessage,
      status: "pending",
      respostas: [],
      budgetMaintenance: budgetMaintenance,
      companyId: props.companyId,
    };
    setIsSubmitting(true);

    try {
      const maintenanceContestResult = await contestMaintenanceCharge(
        props.maintenanceId,
        contestationData
      );

      if (!maintenanceContestResult.ok) {
        notifyError(
          maintenanceContestResult.message || "Falha ao contestar manutenção."
        );
        setIsSubmitting(false);
        return;
      }

      if (props.chargeId) {
        const chargeDisputeResult = await chargesDendDispute(
          props.chargeId,
          fullMessage
        );

        if (!chargeDisputeResult.ok) {
          notifyError(
            chargeDisputeResult.message ||
            "Falha ao registrar contestação da fatura."
          );
          return;
        }
      }

      notifySuccess("Contestação enviada com sucesso!");
      setHistoryChanges([]);
      setContestObservation("");
      props.onClosed();
    } catch {
      notifyError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      props.setReload(!props.reload);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal size="6xl" show={true} onClose={props.onClosed}>
      <Modal.Header>
        <h2 className="text-black">
          Verificar Histórico da Placa: {props.licensePlate}
        </h2>
      </Modal.Header>
      <Modal.Body>
        <MaintenanceHistoryTable
          licensePlate={props.licensePlate}
          areActionsDisabled={areActionsDisabled || false}
          onHistoryChange={setHistoryChanges}
          onFullHistoryUpdate={setFullHistory}
        />

        <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col gap-4">
          {hasHistoryChanges && (
            <textarea
              className="w-full border rounded-md p-2 text-sm"
              rows={4}
              placeholder="Descreva o motivo da contestação..."
              value={contestObservation}
              onChange={(event) => setContestObservation(event.target.value)}
            />
          )}

          <div className="flex justify-end items-center gap-4">
            <Button
              color="warning"
              onClick={handleContest}
              isProcessing={isSubmitting}
              disabled={
                isSubmitting ||
                areActionsDisabled ||
                (hasHistoryChanges && !contestObservation.trim())
              }
            >
              Contestar
            </Button>

            {!hasHistoryChanges && (
              <Button
                color="success"
                onClick={handleConfirm}
                isProcessing={isSubmitting}
                disabled={isSubmitting || areActionsDisabled}
              >
                Confirmar
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ToCheckModal;
